<template>
  <div class="regist">
    <div class="layout">
      <!-- 一覧 -->
      <aside class="list-pane">
        <button class="btn btn-primary block" @click="newEntry">＋ 新規イベント</button>
        <input class="search" v-model="listFilter" type="text" placeholder="タイトルで検索…">
        <div class="list">
          <button
            v-for="item in filteredEvents"
            :key="item.id"
            class="list-item"
            :class="{ active: item.id === editingId }"
            @click="selectEvent(item)">
            <span class="li-date">{{ item.fmtStartDatetime.dateFmt }}</span>
            <span class="li-title">{{ item.title }}</span>
            <span class="li-badges">
              <span v-if="item.hideFlag" class="badge badge-hide">非表示</span>
              <span v-if="item.fullHouseFlag" class="badge badge-full">満員</span>
            </span>
          </button>
          <p v-if="filteredEvents.length === 0" class="empty">該当するイベントがありません</p>
        </div>
      </aside>

      <!-- 入力フォーム -->
      <section class="form-pane">
        <div class="form-head">
          <h2 class="head-title">
            <span class="mode" :class="editingId ? 'mode-edit' : 'mode-new'">
              {{ editingId ? '編集中' : '新規' }}
            </span>
            イベント登録
          </h2>
          <div class="actions">
            <button class="btn btn-ghost" @click="newEntry">新規</button>
            <button class="btn btn-danger" :disabled="!editingId" @click="removeEntry">削除</button>
            <button class="btn btn-primary" @click="save">保存</button>
          </div>
        </div>

        <!-- 基本情報 -->
        <div class="card">
          <div class="card-title">基本情報</div>
          <div class="toggles">
            <label class="check"><input type="checkbox" v-model="form.hideFlag"> 非表示にする</label>
            <label class="check"><input type="checkbox" v-model="form.fullHouseFlag"> 満員御礼</label>
          </div>
          <label class="field">
            <span class="label">タイトル</span>
            <input type="text" v-model="form.title" placeholder="イベント名">
          </label>
          <div class="grid-2">
            <label class="field">
              <span class="label">開始日時</span>
              <input type="datetime-local" v-model="form.startDatetime">
            </label>
            <label class="field">
              <span class="label">終了日時</span>
              <input type="datetime-local" v-model="form.endDatetime">
            </label>
          </div>
          <label class="field">
            <span class="label">内容</span>
            <textarea v-model="form.content" rows="10"
              placeholder="本文。途中に画像を差し込みたい位置に gagagagazou と入力してください。"></textarea>
          </label>
        </div>

        <!-- 価格 -->
        <div class="card">
          <div class="card-title">
            <span>価格</span>
            <label class="check inline"><input type="checkbox" v-model="form.priceDispFlag"> 価格表を表示する</label>
          </div>
          <p class="hint">金額を空欄にした枠は表示されません。表示名を空にすると既定のラベルが使われます。</p>
          <div class="price-slots">
            <div class="price-slot" v-for="slot in priceSlots" :key="slot.key">
              <div class="slot-name">{{ slot.jp }}</div>
              <div class="slot-fields">
                <input type="text" v-model="form.prices[slot.key].label" :placeholder="'表示名（既定: ' + slot.ph + '）'">
                <input type="number" v-model="form.prices[slot.key].price" placeholder="金額">
                <input type="text" v-model="form.prices[slot.key].url" placeholder="申込 / 決済URL">
              </div>
            </div>
          </div>
          <label class="field">
            <span class="label">申込リンク（価格を非表示にした場合）</span>
            <input type="text" v-model="form.applicationUrl" placeholder="https://…">
          </label>
        </div>

        <!-- 画像 -->
        <div class="card">
          <div class="card-title">画像</div>
          <div class="grid-2">
            <div class="img-field">
              <span class="label">メイン画像</span>
              <input type="file" accept="image/*" @change="uploadImage($event, 1)">
              <div class="img-preview" v-if="form.imageurl">
                <img :src="imageUrl(form.imageurl)" alt="メイン画像">
              </div>
            </div>
            <div class="img-field">
              <span class="label">差込画像（本文の gagagagazou の位置に表示）</span>
              <input type="file" accept="image/*" @change="uploadImage($event, 2)">
              <div class="img-preview" v-if="form.imageurl2">
                <img :src="imageUrl(form.imageurl2)" alt="差込画像">
              </div>
            </div>
          </div>
        </div>

        <div class="actions actions-bottom">
          <button class="btn btn-ghost" @click="newEntry">新規</button>
          <button class="btn btn-danger" :disabled="!editingId" @click="removeEntry">削除</button>
          <button class="btn btn-primary" @click="save">保存</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import firebase from "../main.js";
import { collection, doc, addDoc, setDoc, getDocs, deleteDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes } from "firebase/storage";

const BUCKET = "farmy-demo.appspot.com";
const STORAGE_DIR = "events-contents-image/";

// 表示キー / Firestore フィールド接頭辞 / 日本語名 / 既定ラベル
const PRICE_SLOTS = [
  { key: 'premium',    field: 'premium',           jp: 'プレミアム',           ph: 'プレミアム会員価格' },
  { key: 'basic',      field: 'basic',             jp: 'ベーシック',           ph: 'ベーシック会員価格' },
  { key: 'other',      field: 'other',             jp: 'サポーター＆非会員',   ph: 'サポーター＆非会員価格' },
  { key: 'student',    field: 'student',           jp: '小中高',               ph: '小中高価格' },
  { key: 'elementary', field: 'elementaryStudent', jp: '小学生',               ph: '小学生価格' },
  { key: 'baby',       field: 'baby',              jp: '未就学児',             ph: '未就学児価格' },
];

function emptyPrices() {
  const p = {};
  PRICE_SLOTS.forEach((s) => { p[s.key] = { label: '', price: '', url: '' }; });
  return p;
}

function emptyForm() {
  return {
    hideFlag: false,
    fullHouseFlag: false,
    title: '',
    content: '',
    startDatetime: '',
    endDatetime: '',
    priceDispFlag: true,
    imageurl: '',
    imageurl2: '',
    applicationUrl: '',
    prices: emptyPrices(),
  };
}

export default {
  name: 'EventRegist',
  data() {
    return {
      events: [],
      editingId: null,
      listFilter: '',
      priceSlots: PRICE_SLOTS,
      form: emptyForm(),
    };
  },
  computed: {
    filteredEvents() {
      const q = this.listFilter.trim().toLowerCase();
      if (!q) return this.events;
      return this.events.filter((e) => (e.title || '').toLowerCase().includes(q));
    },
  },
  mounted() {
    this.fetchEvents();
  },
  methods: {
    toDateTime(datetime) {
      var date = new Date(1970, 0, 1);
      date.setSeconds(datetime.seconds);
      date.setHours(date.getHours() + 9);
      return {
        dateFmt: date.getFullYear() + "/" + ("00" + (date.getMonth() + 1)).slice(-2) + "/" + ("00" + date.getDate()).slice(-2),
        timeFmt: ("00" + date.getHours()).slice(-2) + ":" + ("00" + date.getMinutes()).slice(-2),
        datetimeVal: date.getFullYear()
          + "-" + ("00" + (date.getMonth() + 1)).slice(-2)
          + "-" + ("00" + date.getDate()).slice(-2)
          + "T" + ("00" + date.getHours()).slice(-2)
          + ":" + ("00" + date.getMinutes()).slice(-2),
      };
    },
    async fetchEvents() {
      this.events = [];
      const snap = await getDocs(collection(firebase.db, "Events"));
      snap.forEach((d) => {
        const data = d.data();
        data.id = d.id;
        data.fmtStartDatetime = this.toDateTime(data.startDatetime);
        data.fmtEndDatetime = this.toDateTime(data.endDatetime);
        this.events.push(data);
      });
      this.events.sort((a, b) => b.startDatetime - a.startDatetime);
    },
    newEntry() {
      this.editingId = null;
      this.form = emptyForm();
    },
    selectEvent(item) {
      this.editingId = item.id;
      const f = emptyForm();
      f.hideFlag = !!item.hideFlag;
      f.fullHouseFlag = !!item.fullHouseFlag;
      f.title = item.title || '';
      f.content = item.content || '';
      f.startDatetime = item.fmtStartDatetime.datetimeVal;
      f.endDatetime = item.fmtEndDatetime.datetimeVal;
      f.priceDispFlag = ('priceDispFlag' in item) ? item.priceDispFlag : true;
      f.imageurl = item.imageurl || '';
      f.imageurl2 = item.imageurl2 || '';
      f.applicationUrl = item.applicationUrl || '';
      PRICE_SLOTS.forEach((s) => {
        f.prices[s.key].label = item[`${s.field}Label`] || '';
        f.prices[s.key].price = (item[`${s.field}Price`] == null) ? '' : String(item[`${s.field}Price`]);
        f.prices[s.key].url = item[`${s.field}Url`] || '';
      });
      this.form = f;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    buildData() {
      const f = this.form;
      const d = {
        hideFlag: f.hideFlag,
        fullHouseFlag: f.fullHouseFlag,
        title: f.title,
        content: f.content,
        startDatetime: new Date(f.startDatetime),
        endDatetime: new Date(f.endDatetime),
        updateDate: new Date(),
        imageurl: f.imageurl,
        imageurl2: f.imageurl2,
        priceDispFlag: f.priceDispFlag,
        applicationUrl: f.applicationUrl,
      };
      PRICE_SLOTS.forEach((s) => {
        d[`${s.field}Label`] = f.prices[s.key].label;
        d[`${s.field}Price`] = f.prices[s.key].price;
        d[`${s.field}Url`] = f.prices[s.key].url;
      });
      return d;
    },
    async save() {
      if (!this.form.title.trim()) { alert("タイトルを入力してください"); return; }
      if (!this.form.startDatetime || !this.form.endDatetime) { alert("開始・終了日時を入力してください"); return; }
      const data = this.buildData();
      if (this.editingId) {
        await setDoc(doc(firebase.db, "Events", this.editingId), data);
        alert("イベントを更新しました");
      } else {
        data.createDate = new Date();
        await addDoc(collection(firebase.db, "Events"), data);
        alert("イベントを登録しました");
      }
      await this.fetchEvents();
      this.newEntry();
    },
    async removeEntry() {
      if (!this.editingId) return;
      if (!confirm(`「${this.form.title}」を削除してもいいですか？`)) return;
      await deleteDoc(doc(firebase.db, "Events", this.editingId));
      alert("削除しました");
      await this.fetchEvents();
      this.newEntry();
    },
    imageUrl(filename) {
      if (!filename) return '';
      const path = encodeURIComponent(STORAGE_DIR + filename);
      return `https://firebasestorage.googleapis.com/v0/b/${BUCKET}/o/${path}?alt=media`;
    },
    uploadImage(event, which) {
      const file = event.target.files[0];
      if (!file) return;
      const storage = getStorage();
      const storageRef = ref(storage, STORAGE_DIR + file.name);
      uploadBytes(storageRef, file).then((snapshot) => {
        const name = snapshot.metadata.name;
        if (which === 2) this.form.imageurl2 = name;
        else this.form.imageurl = name;
      });
    },
  },
};
</script>

<style scoped>
.regist {
  --gold: #AF9453;
  --gold-dark: #937b3f;
  --danger: #c0392b;
  --border: #e3ddcd;
  --bg: #faf8f3;
  --text: #3a3630;
  color: var(--text);
  text-align: left;
  padding: 16px;
  max-width: 1200px;
  margin: 0 auto;
}
.layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

/* 一覧 */
.list-pane {
  width: 300px;
  flex-shrink: 0;
  position: sticky;
  top: 12px;
}
.search {
  width: 100%;
  margin: 10px 0;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
}
.list {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
  max-height: 70vh;
  overflow-y: auto;
}
.list-item {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: "date badges" "title title";
  gap: 2px 8px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid #f0ece1;
  background: #fff;
  cursor: pointer;
}
.list-item:hover { background: var(--bg); }
.list-item.active { background: #f3ecd9; box-shadow: inset 3px 0 0 var(--gold); }
.li-date { grid-area: date; font-size: 12px; color: #9a927f; }
.li-title { grid-area: title; font-size: 14px; font-weight: 600; line-height: 1.3; }
.li-badges { grid-area: badges; display: inline-flex; gap: 4px; }
.badge { font-size: 10px; padding: 1px 6px; border-radius: 999px; color: #fff; }
.badge-hide { background: #9a927f; }
.badge-full { background: var(--danger); }
.empty { padding: 16px; color: #9a927f; font-size: 13px; }

/* フォーム */
.form-pane { flex: 1; min-width: 0; }
.form-head {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: rgba(250, 248, 243, 0.95);
  backdrop-filter: blur(4px);
  padding: 8px 0 12px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--border);
}
.head-title { font-size: 20px; font-weight: 700; display: flex; align-items: center; gap: 10px; }
.mode { font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px; }
.mode-new { background: #eef4e8; color: #4d7a2f; }
.mode-edit { background: #f3ecd9; color: var(--gold-dark); }

.actions { display: inline-flex; gap: 8px; }
.actions-bottom { justify-content: flex-end; margin-top: 16px; }

.card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 16px;
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--gold-dark);
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0ece1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.hint { font-size: 12px; color: #9a927f; margin: -6px 0 12px; }

.field { display: block; margin-bottom: 14px; }
.label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 5px; color: #6b6555; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

input[type="text"],
input[type="number"],
input[type="datetime-local"],
textarea {
  width: 100%;
  padding: 9px 11px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: #fff;
  box-sizing: border-box;
}
input:focus, textarea:focus {
  outline: none;
  border-color: var(--gold);
  box-shadow: 0 0 0 3px rgba(175, 148, 83, 0.15);
}
textarea { resize: vertical; line-height: 1.5; }

.toggles { display: flex; gap: 20px; margin-bottom: 14px; }
.check { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; cursor: pointer; }
.check.inline { font-size: 13px; font-weight: 500; color: #6b6555; }
.check input { width: 16px; height: 16px; accent-color: var(--gold); }

/* 価格枠 */
.price-slots { display: flex; flex-direction: column; gap: 10px; margin-bottom: 14px; }
.price-slot {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
  align-items: center;
  padding: 10px;
  background: var(--bg);
  border-radius: 8px;
}
.slot-name { font-size: 13px; font-weight: 700; color: #6b6555; }
.slot-fields { display: grid; grid-template-columns: 1.4fr 0.8fr 1.4fr; gap: 8px; }

/* 画像 */
.img-field { display: flex; flex-direction: column; gap: 8px; }
.img-field input[type="file"] { font-size: 13px; }
.img-preview img {
  max-width: 100%;
  max-height: 180px;
  border-radius: 8px;
  border: 1px solid var(--border);
}

/* ボタン */
.btn {
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: filter 0.15s, background 0.15s;
}
.btn.block { width: 100%; }
.btn-primary { background: var(--gold); color: #fff; }
.btn-primary:hover { filter: brightness(1.06); }
.btn-ghost { background: #fff; color: var(--gold-dark); border-color: var(--border); }
.btn-ghost:hover { background: var(--bg); }
.btn-danger { background: #fff; color: var(--danger); border-color: #e3b7b1; }
.btn-danger:hover { background: #fdeeec; }
.btn-danger:disabled { color: #c9c3b5; border-color: var(--border); cursor: not-allowed; background: #fff; }

/* レスポンシブ */
@media screen and (max-width: 860px) {
  .layout { flex-direction: column; }
  .list-pane { width: 100%; position: static; }
  .list { max-height: 260px; }
  .grid-2 { grid-template-columns: 1fr; }
  .price-slot { grid-template-columns: 1fr; gap: 8px; }
  .slot-fields { grid-template-columns: 1fr; }
}
</style>
